import { useI18n } from '../i18n.tsx';
import { STATUS_ORDER } from '../constants.ts';
import type { Meta, Terrain, VerificationStatus } from '../types.ts';

export interface FilterState {
  q: string;
  month: string;
  county: string;
  terrain: string;
  status: string;
  maxKm: string;
}

export const EMPTY_FILTERS: FilterState = {
  q: '',
  month: '',
  county: '',
  terrain: '',
  status: '',
  maxKm: '',
};

export function Filters({
  value,
  meta,
  onChange,
  onReset,
}: {
  value: FilterState;
  meta: Meta | undefined;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}) {
  const { t } = useI18n();
  const set = <K extends keyof FilterState>(key: K, next: FilterState[K]) =>
    onChange({ ...value, [key]: next });

  const dirty = Object.values(value).some((v) => v !== '');

  return (
    <div className="filters">
      <div className="field">
        <label htmlFor="f-q">{t.searchPlaceholder}</label>
        <input
          id="f-q"
          type="search"
          value={value.q}
          placeholder={t.searchPlaceholder}
          onChange={(e) => set('q', e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="f-month">{t.month}</label>
        <select id="f-month" value={value.month} onChange={(e) => set('month', e.target.value)}>
          <option value="">{t.all}</option>
          {meta?.months.map((m) => (
            <option key={m} value={String(m)}>
              {t.monthNames[m - 1]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="f-county">{t.county}</label>
        <select id="f-county" value={value.county} onChange={(e) => set('county', e.target.value)}>
          <option value="">{t.all}</option>
          {meta?.counties.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="f-terrain">{t.terrain}</label>
        <select
          id="f-terrain"
          value={value.terrain}
          onChange={(e) => set('terrain', e.target.value)}
        >
          <option value="">{t.all}</option>
          {meta?.terrains.map((terrain: Terrain) => (
            <option key={terrain} value={terrain}>
              {t.terrainLabels[terrain]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="f-status">{t.status}</label>
        <select id="f-status" value={value.status} onChange={(e) => set('status', e.target.value)}>
          <option value="">{t.all}</option>
          {STATUS_ORDER.map((status: VerificationStatus) => (
            <option key={status} value={status}>
              {t.statusLabels[status]}
            </option>
          ))}
        </select>
      </div>

      <button type="button" className="link-button" onClick={onReset} disabled={!dirty}>
        {t.reset}
      </button>
    </div>
  );
}
